import type { Board, Task } from './types'

/**
 * **同じタスクが別 ID で複数ある**問題をまとめる。→ doc/task-board/master.md#同じタスクの別版
 *
 * tarkov.dev のデータには、名前もトレーダーも目標も同じなのに ID が違うタスクが 517件中
 * 15件ある（`textile-part-1-bear` / `-usec` のような陣営違い、`make-amends-2` のような複製）。
 * 検索結果ではまったく同じ行に見えるので、**2人が別々に登録すると別 ID が2件登録され**、
 * 一覧に同じ名前の行が2つ並び、マップにも同じ場所の図形が2つ重なって出る。
 *
 * そこで、内容が同じものは1件（正）にまとめ、残りは正のIDへ読み替える。
 * React にも DOM にも依存しないので単体で検証できる（`npm run test:variants`）。
 */

/**
 * 「中身が同じタスク」を判定するキー。**表示言語に依らない要素だけで作る。**
 *
 * 目標の本文は翻訳の当たり具合で変わる（同じタスクでも片方だけ英語のことがある）ので使わない。
 * 代わりに目標の**種別と件数**、それに**関係するマップ**を見る。
 * これで陣営違い・複製だけがまとまり、名前が同じでも中身が違うもの
 * （`New Beginning` のレベル違い4種、`The Tarkov Shooter - Part 5` の customs 版と streets 版）は
 * 別のままになる。
 */
export function variantKey(task: Task): string {
  return [
    task.name,
    task.trader,
    task.objectives.length,
    task.objectives
      .map((o) => o.type)
      .sort()
      .join(','),
    [...task.maps].sort().join(','),
  ].join('|')
}

/** 図形を持つものを正にする（まとめた結果マップに何も出なくなるのを防ぐ） */
const zoneCount = (task: Task) => task.objectives.reduce((n, o) => n + o.zones.length, 0)

export type MergedTasks = {
  /** 正だけになったタスク。並びは元のまま */
  tasks: Task[]
  /** 別版のID → 正のID。まとめるものが無ければ空 */
  canonicalOf: Map<string, string>
}

/**
 * 中身が同じタスクを1件にまとめる。
 * 正は**図形を多く持つもの**、同数なら ID の小さいほう（毎回同じ結果になるように）。
 */
export function mergeTaskVariants(tasks: Task[]): MergedTasks {
  const groups = new Map<string, Task[]>()
  for (const task of tasks) {
    const key = variantKey(task)
    const list = groups.get(key)
    if (list) list.push(task)
    else groups.set(key, [task])
  }

  const canonicalOf = new Map<string, string>()
  const canonical = new Set<string>()
  for (const list of groups.values()) {
    const head = list.reduce((best, t) =>
      zoneCount(t) > zoneCount(best) || (zoneCount(t) === zoneCount(best) && t.id < best.id) ? t : best,
    )
    canonical.add(head.id)
    if (list.length > 1) for (const t of list) if (t.id !== head.id) canonicalOf.set(t.id, head.id)
  }

  return { tasks: canonicalOf.size ? tasks.filter((t) => canonical.has(t.id)) : tasks, canonicalOf }
}

/**
 * ボードの中のタスクIDを正のIDに読み替え、重複を落とす。
 *
 * すでに別版が登録されているボード（この処理を入れる前に作られたもの、
 * あるいは古い版のクライアントが書いたもの）もこれで1件に見える。
 * **読み替えた結果は次に誰かがボードを触ったときにサーバへも書き戻る。**
 */
export function canonicalizeBoard(board: Board, canonicalOf: Map<string, string>): Board {
  if (canonicalOf.size === 0) return board
  const ids = (list: string[]) => [...new Set(list.map((id) => canonicalOf.get(id) ?? id))]
  return {
    ...board,
    slots: board.slots.map((s) => ({ ...s, taskIds: ids(s.taskIds) })),
    order: ids(board.order),
    hidden: ids(board.hidden),
  }
}
